<form [formGroup]="formGroup">
  <div class="space-y-12">
    <div class="border-b border-gray-900/10 pb-2">
      <h2 class="primary-sub-title">DTC {{ item?.displayId }}</h2>
      <p class="secondary-small-text mb-2">
        {{ item?.swPartNumber?.displayId }}
      </p>
      <div class="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div class="col-span-full grid gap-6 mt-6 md:grid-cols-4">
          <div>
            <app-label-with-help for="priority" class="primary-label" fieldConfigurationAddress="dtc.priority"
              >Priority</app-label-with-help
            >
            <div class="relative">
              <input
                type="number"
                id="priority"
                name="priority"
                class="primary-machine-field"
                placeholder="e.g. 0 - 255"
                min="0"
                max="255"
                formControlName="priority"
                [readonly]="basicEditDisabled"
                appTrackUnsavedField
                [reset$]="resetFieldTracking$"
              />
            </div>
            @if (formGroup.controls.priority.touched && formGroup.controls.priority.hasError('min')) {
              <span class="text-red-600 text-sm font-extralight">0 is the lowest value</span>
            }
            @if (formGroup.controls.priority.touched && formGroup.controls.priority.hasError('max')) {
              <span class="text-red-600 text-sm font-extralight">255 is the highest value</span>
            }
            @if (formGroup.controls.priority.touched && formGroup.controls.priority.hasError('required')) {
              <span class="text-red-600 text-sm font-extralight">A value is required</span>
            }
          </div>
          <div>
            <app-label-with-help for="group" class="primary-label" fieldConfigurationAddress="dtc.group"
              >Group</app-label-with-help
            >
            <div class="relative">
              <input
                type="number"
                id="group"
                name="group"
                class="primary-machine-field"
                placeholder="e.g. 0 - 255"
                min="0"
                max="255"
                formControlName="group"
                [readonly]="basicEditDisabled"
                appTrackUnsavedField
                [reset$]="resetFieldTracking$"
              />
            </div>
            @if (formGroup.controls.group.touched && formGroup.controls.group.hasError('min')) {
              <span class="text-red-600 text-sm font-extralight">0 is the lowest value</span>
            }
            @if (formGroup.controls.group.touched && formGroup.controls.group.hasError('max')) {
              <span class="text-red-600 text-sm font-extralight">255 is the highest value</span>
            }
            @if (formGroup.controls.group.touched && formGroup.controls.group.hasError('required')) {
              <span class="text-red-600 text-sm font-extralight">A value is required</span>
            }
          </div>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="implementationHint"
            [label]="'Implementation Hint'"
            [fieldConfigurationAddress]="'dtc.implementationHint'"
            [fieldIsRequired]="true"
            [borderColorClass]="'border-machine-field focus:ring-machine-field focus:border-machine-field'"
            [readonly]="basicEditDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="inline-flex items-center justify-center col-span-full relative">
          <hr class="w-64 h-px my-8 bg-gray-200 border-0" />
          <span class="absolute px-3 font-normal text-gray-500 -translate-x-1/2 bg-white left-1/2">optional</span>
        </div>

        <div class="col-span-full grid gap-6 md:grid-cols-6">
          <div class="col-span-5">
            <app-label-with-help for="priority" class="primary-label" fieldConfigurationAddress="dtc.errorMessage"
              >Link to error message</app-label-with-help
            >
            <div class="relative">
              <app-error-message-selection
                appTrackUnsavedField
                [reset$]="resetFieldTracking$"
                [selectedErrorMessageId]="$safeNavigationMigration(item?.errorMessageId)"
                [dtcId]="$safeNavigationMigration(item?.id)"
                [showCreateErrorMessage]="true"
                formControlName="errorMessageId"
                (unsavedChanges)="errorMessageSelectionHasUnsavedChanges = $event"
                [disabled]="swPartNumberIsLocked"
                [unsavedChanges]="errorMessageSelectionHasUnsavedChanges"
              ></app-error-message-selection>
            </div>
          </div>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="detailsOnTerminal"
            [warning]="translatableFieldsTerminalWarning"
            [label]="'Details on Terminal'"
            [isTranslatable]="isNotDefaultLanguage"
            [fieldConfigurationAddress]="'dtc.detailsOnTerminal'"
            [fieldIsRequired]="false"
            [readonly]="basicEditDisabled || updateTranslationFieldDisabled"
            [borderColorClass]="'border-terminal-field focus:ring-terminal-field focus:border-terminal-field'"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="causeFarmer"
            [warning]="translatableFieldsDocumentationWarning"
            [label]="'Cause (Farmer)'"
            [isTranslatable]="isNotDefaultLanguage"
            [fieldConfigurationAddress]="'dtc.causeFarmer'"
            [rows]="3"
            [readonly]="elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="remedyFarmer"
            [warning]="translatableFieldsDocumentationWarning"
            [label]="'Remedy (Farmer)'"
            [isTranslatable]="isNotDefaultLanguage"
            [fieldConfigurationAddress]="'dtc.remedyFarmer'"
            [rows]="3"
            [readonly]="elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="causeDealer"
            [label]="'Cause (Dealer)'"
            [isTranslatable]="isNotDefaultLanguage"
            [fieldConfigurationAddress]="'dtc.causeDealer'"
            [rows]="3"
            [readonly]="elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="remedyDealer"
            [label]="'Remedy (Dealer)'"
            [isTranslatable]="isNotDefaultLanguage"
            [fieldConfigurationAddress]="'dtc.remedyDealer'"
            [rows]="3"
            [readonly]="elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="causeIntern"
            [label]="'Cause (Intern)'"
            [isTranslatable]="isNotDefaultLanguage"
            [fieldConfigurationAddress]="'dtc.causeIntern'"
            [rows]="3"
            [readonly]="elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="remedyIntern"
            [label]="'Remedy (Intern)'"
            [isTranslatable]="isNotDefaultLanguage"
            [fieldConfigurationAddress]="'dtc.remedyIntern'"
            [rows]="3"
            [readonly]="elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            formControlName="internalNotes"
            [label]="'Internal notes'"
            [fieldConfigurationAddress]="'dtc.internalNotes'"
            [fieldIsRequired]="false"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full flex justify-end">
          <button
            type="button"
            [disabled]="formGroup.invalid || swPartNumberIsLocked"
            (click)="requestPropagationOfChangesToEntitiesInOtherVersions()"
            class="crud-btn-sidebar"
            appAgentAction
            agent="dtc-edit-save"
            businessObject="dtc"
            [businessId]="$safeNavigationMigration(item?.id)"
            action="save"
          >
            <app-floppy-icon></app-floppy-icon>
          </button>
        </div>
      </div>
    </div>
  </div>
</form>
<app-propagate-dtc-changes-to-entities-in-other-versions-modal
  #propagateChangesToEntitiesInOtherVersions
  (changesConfirmed)="confirmChanges($event)"
  (notPropagateChanges)="confirmChanges()"
></app-propagate-dtc-changes-to-entities-in-other-versions-modal>
